import {
  createContext,
  forwardRef,
  Fragment,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type BaseSyntheticEvent,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { flatten, sx, type Style, type StyleProp } from './style';

/**
 * The DOM primitives the ported screens are written against.
 *
 * Each one renders a real, semantic element — `View` is a `<div>`, `Pressable`
 * is a `<button>`, `TextInput` is an `<input>` — and applies the RN box model
 * defaults (column flex, `flex-shrink: 0`) that the mobile layout numbers assume.
 * The base defaults live in styles/global.css as `.rn-*` classes so they are one
 * cached stylesheet rather than a style attribute on every node.
 */

/* -------------------------------------------------------------------------- */
/* View                                                                       */
/* -------------------------------------------------------------------------- */

export type ViewProps = {
  children?: ReactNode;
  style?: StyleProp;
  className?: string;
  id?: string;
  onLayout?: (e: { nativeEvent: { layout: { width: number; height: number } } }) => void;
  accessibilityRole?: string;
  accessibilityLabel?: string;
  pointerEvents?: 'auto' | 'none' | 'box-none';
  testID?: string;
};

export const View = forwardRef<HTMLDivElement, ViewProps>(function View(
  { children, style, className, id, onLayout, accessibilityRole, accessibilityLabel, pointerEvents, testID },
  ref,
) {
  const inner = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(ref, () => inner.current as HTMLDivElement);

  useEffect(() => {
    if (!onLayout || !inner.current) return undefined;
    const el = inner.current;
    const emit = () =>
      onLayout({ nativeEvent: { layout: { width: el.offsetWidth, height: el.offsetHeight } } });
    emit();
    const ro = new ResizeObserver(emit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onLayout]);

  return (
    <div
      ref={inner}
      id={id}
      data-testid={testID}
      className={cx('rn-view', pointerEvents === 'box-none' && 'rn-box-none', className)}
      role={accessibilityRole}
      aria-label={accessibilityLabel}
      style={{
        ...(pointerEvents && pointerEvents !== 'box-none' ? { pointerEvents } : null),
        ...sx(style),
      }}
    >
      {children}
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/* Text                                                                       */
/* -------------------------------------------------------------------------- */

/** Nested <Text> is inline in RN, exactly as nested inline text is on the web. */
const TextAncestor = createContext(false);

export type TextProps = {
  children?: ReactNode;
  style?: StyleProp;
  /** 1 = one-line ellipsis; n > 1 = clamp to n lines. */
  numberOfLines?: number;
  onPress?: () => void;
  accessibilityRole?: string;
  selectable?: boolean;
  testID?: string;
};

export function Text({
  children,
  style,
  numberOfLines,
  onPress,
  accessibilityRole,
  selectable,
  testID,
}: TextProps) {
  const nested = useContext(TextAncestor);

  const clamp: CSSProperties | null =
    numberOfLines === 1
      ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
      : numberOfLines && numberOfLines > 1
        ? {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: numberOfLines,
            overflow: 'hidden',
          }
        : null;

  const node = (
    <span
      data-testid={testID}
      className={cx('rn-text', nested && 'rn-text-nested', onPress && 'rn-text-pressable')}
      role={accessibilityRole ?? (onPress ? 'button' : undefined)}
      tabIndex={onPress ? 0 : undefined}
      onClick={onPress}
      onKeyDown={
        onPress
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPress();
              }
            }
          : undefined
      }
      style={{
        ...(selectable === false ? { userSelect: 'none' } : null),
        ...sx(style),
        ...clamp,
      }}
    >
      {children}
    </span>
  );

  return nested ? node : <TextAncestor.Provider value>{node}</TextAncestor.Provider>;
}

/* -------------------------------------------------------------------------- */
/* Pressable                                                                  */
/* -------------------------------------------------------------------------- */

/** A <button> may not contain a <button>; nested pressables degrade to role="button". */
const PressableAncestor = createContext(false);

type PressState = { pressed: boolean; hovered: boolean };

export type PressableProps = {
  children?: ReactNode | ((state: PressState) => ReactNode);
  style?: StyleProp | ((state: PressState) => StyleProp);
  /** `unknown` return so an `async` handler (or a react-hook-form submit,
   *  which returns a promise) can be passed straight in. */
  onPress?: (e: GestureResponderEvent) => unknown;
  onLongPress?: () => void;
  /** How long a press must be held before `onLongPress` fires. */
  delayLongPress?: number;
  disabled?: boolean;
  /** Extra touch area outside the visual box, like RN's. */
  hitSlop?: number;
  /** RN's: `false` takes the box out of the accessibility tree. Used for a
   *  press that is a GESTURE rather than a control — a double tap on a photo —
   *  where announcing a button and taking a tab stop would offer a keyboard or
   *  a screen reader something it cannot perform. Renders a plain box instead
   *  of a button, keeping only the pointer behaviour. */
  accessible?: boolean;
  accessibilityRole?: string;
  accessibilityLabel?: string;
  accessibilityState?: { selected?: boolean; disabled?: boolean; checked?: boolean };
  accessibilityHint?: string;
  /** Android's touch ripple. The web's press feedback is the `pressed` state
   *  the style callback already receives, so this is accepted and ignored. */
  android_ripple?: { color?: string; borderless?: boolean };
  className?: string;
  testID?: string;
};

export function Pressable({
  children,
  style,
  onPress,
  onLongPress,
  delayLongPress = 500,
  disabled,
  hitSlop,
  accessible,
  accessibilityRole,
  accessibilityLabel,
  accessibilityState,
  accessibilityHint,
  className,
  testID,
}: PressableProps) {
  const nested = useContext(PressableAncestor);
  /** A gesture surface, not a control — see `accessible` on PressableProps. */
  const gesture = accessible === false;
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const longPressTimer = useRef<number | null>(null);

  const state: PressState = { pressed, hovered };
  const resolved = typeof style === 'function' ? style(state) : style;
  const content = typeof children === 'function' ? children(state) : children;

  const startLongPress = () => {
    if (!onLongPress) return;
    longPressTimer.current = window.setTimeout(() => onLongPress(), delayLongPress);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const shared = {
    'data-testid': testID,
    className: cx('rn-view', gesture ? 'rn-gesture' : 'rn-pressable', hitSlop ? 'rn-hitslop' : null, className),
    'aria-label': accessibilityLabel,
    'aria-selected': accessibilityState?.selected,
    'aria-checked': accessibilityState?.checked,
    'aria-disabled': disabled || undefined,
    'aria-describedby': undefined,
    title: accessibilityHint,
    style: {
      ...(hitSlop ? ({ ['--rn-hit' as string]: -hitSlop + 'px' } as CSSProperties) : null),
      ...sx(resolved),
    },
    onPointerDown: () => {
      if (disabled) return;
      setPressed(true);
      startLongPress();
    },
    onPointerUp: () => {
      setPressed(false);
      cancelLongPress();
    },
    onPointerLeave: () => {
      setPressed(false);
      setHovered(false);
      cancelLongPress();
    },
    onPointerEnter: () => setHovered(true),
    onClick: (e: BaseSyntheticEvent) => {
      if (disabled) return;
      onPress?.(e);
    },
  };

  const node = gesture ? (
    <div {...shared}>{content}</div>
  ) : nested ? (
    <span
      {...shared}
      role={accessibilityRole ?? 'button'}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPress?.(e);
        }
      }}
    >
      {content}
    </span>
  ) : (
    <button {...shared} type="button" disabled={disabled} role={accessibilityRole}>
      {content}
    </button>
  );

  // A gesture box is not a button, so a Pressable inside it may still be one.
  return <PressableAncestor.Provider value={gesture ? nested : true}>{node}</PressableAncestor.Provider>;
}

/* -------------------------------------------------------------------------- */
/* ScrollView (+ pull-to-refresh)                                             */
/* -------------------------------------------------------------------------- */

export type RefreshControlProps = { refreshing: boolean; onRefresh?: () => void; tintColor?: string };

/**
 * Descriptor only — `ScrollView` reads the props off the element it is handed
 * (`refreshControl={<RefreshControl … />}`), exactly like RN does, and draws the
 * indicator itself.
 */
export function RefreshControl(_props: RefreshControlProps) {
  return null;
}

export type ScrollViewRef = {
  scrollToEnd: (opts?: { animated?: boolean }) => void;
  scrollTo: (opts: { x?: number; y?: number; animated?: boolean }) => void;
};

export type ScrollViewProps = {
  children?: ReactNode;
  style?: StyleProp;
  contentContainerStyle?: StyleProp;
  horizontal?: boolean;
  showsVerticalScrollIndicator?: boolean;
  showsHorizontalScrollIndicator?: boolean;
  scrollEnabled?: boolean;
  /** RN snaps each child to a full page. CSS scroll-snap is the browser's
   *  equivalent and needs no JS — see the `rn-scroll-paged` class. */
  pagingEnabled?: boolean;
  /** RN fires this when a fling settles. The browser has no momentum event, so
   *  it is synthesised from `scrollend` where that exists and from a debounced
   *  `scroll` everywhere else. */
  onMomentumScrollEnd?: (e: { nativeEvent: { contentOffset: { x: number; y: number } } }) => void;
  refreshControl?: React.ReactElement<RefreshControlProps> | null;
  keyboardShouldPersistTaps?: string;
  onContentSizeChange?: (w: number, h: number) => void;
  onLayout?: ViewProps['onLayout'];
  onScroll?: (e: { nativeEvent: { contentOffset: { x: number; y: number } } }) => void;
  /** RN throttles scroll events across the bridge; the browser has no bridge. */
  scrollEventThrottle?: number;
  keyboardDismissMode?: string;
  /** iOS-only inset for the scroll indicator; the browser draws its own. */
  scrollIndicatorInsets?: { top?: number; bottom?: number; left?: number; right?: number };
  accessibilityLabel?: string;
  accessibilityRole?: string;
  className?: string;
  testID?: string;
};

const PULL_THRESHOLD = 64;

export const ScrollView = forwardRef<ScrollViewRef, ScrollViewProps>(function ScrollView(
  {
    children,
    style,
    contentContainerStyle,
    horizontal,
    showsVerticalScrollIndicator,
    showsHorizontalScrollIndicator,
    scrollEnabled = true,
    pagingEnabled,
    onMomentumScrollEnd,
    refreshControl,
    onContentSizeChange,
    onScroll,
    accessibilityLabel,
    accessibilityRole,
    className,
    testID,
  },
  ref,
) {
  const el = useRef<HTMLDivElement | null>(null);
  const content = useRef<HTMLDivElement | null>(null);
  const [pull, setPull] = useState(0);
  const pullStart = useRef<number | null>(null);

  const refresh = refreshControl?.props;
  const refreshing = refresh?.refreshing ?? false;

  useImperativeHandle(ref, () => ({
    scrollToEnd: ({ animated = true } = {}) => {
      const node = el.current;
      if (!node) return;
      node.scrollTo({ top: node.scrollHeight, behavior: animated ? 'smooth' : 'auto' });
    },
    scrollTo: ({ x = 0, y = 0, animated = true }) => {
      el.current?.scrollTo({ left: x, top: y, behavior: animated ? 'smooth' : 'auto' });
    },
  }));

  useEffect(() => {
    if (!onContentSizeChange || !content.current) return undefined;
    const node = content.current;
    const emit = () => onContentSizeChange(node.scrollWidth, node.scrollHeight);
    emit();
    const ro = new ResizeObserver(emit);
    ro.observe(node);
    return () => ro.disconnect();
  }, [onContentSizeChange]);

  // Pull-to-refresh, only when a RefreshControl was supplied and only from the top.
  const onTouchStart = (e: React.TouchEvent) => {
    if (!refresh?.onRefresh || horizontal) return;
    pullStart.current = el.current && el.current.scrollTop <= 0 ? e.touches[0].clientY : null;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (pullStart.current === null) return;
    const delta = e.touches[0].clientY - pullStart.current;
    setPull(delta > 0 ? Math.min(delta * 0.5, PULL_THRESHOLD * 1.5) : 0);
  };
  const onTouchEnd = () => {
    if (pull >= PULL_THRESHOLD) refresh?.onRefresh?.();
    pullStart.current = null;
    setPull(0);
  };

  // `scrollend` is not in every browser yet, so a debounce is the floor. Both
  // paths report the same shape RN's onMomentumScrollEnd does, so the caller
  // never learns which one fired.
  const settle = useRef<number | undefined>(undefined);
  const emitSettled = (node: HTMLDivElement) => {
    onMomentumScrollEnd?.({
      nativeEvent: { contentOffset: { x: node.scrollLeft, y: node.scrollTop } },
    });
  };
  useEffect(() => {
    const node = el.current;
    if (!onMomentumScrollEnd || !node) return undefined;
    if ('onscrollend' in window) {
      const h = () => emitSettled(node);
      node.addEventListener('scrollend', h);
      return () => node.removeEventListener('scrollend', h);
    }
    const h = () => {
      window.clearTimeout(settle.current);
      settle.current = window.setTimeout(() => emitSettled(node), 120);
    };
    node.addEventListener('scroll', h);
    return () => {
      node.removeEventListener('scroll', h);
      window.clearTimeout(settle.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMomentumScrollEnd]);

  const hideBars = horizontal
    ? showsHorizontalScrollIndicator === false
    : showsVerticalScrollIndicator === false;

  return (
    <div
      ref={el}
      data-testid={testID}
      className={cx(
        'rn-view',
        'rn-scroll',
        horizontal ? 'rn-scroll-h' : 'rn-scroll-v',
        hideBars && 'rn-scroll-hidebars',
        pagingEnabled && 'rn-scroll-paged',
        className,
      )}
      style={{ ...(scrollEnabled ? null : { overflow: 'hidden' }), ...sx(style) }}
      aria-label={accessibilityLabel}
      role={accessibilityRole}
      onScroll={
        onScroll
          ? (e) =>
              onScroll({
                nativeEvent: {
                  contentOffset: { x: e.currentTarget.scrollLeft, y: e.currentTarget.scrollTop },
                },
              })
          : undefined
      }
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {refresh?.onRefresh ? (
        <div
          className="rn-refresh"
          style={{ height: refreshing ? PULL_THRESHOLD * 0.6 : pull, color: refresh.tintColor }}
          aria-hidden={!refreshing}
        >
          {(refreshing || pull > 8) && <ActivityIndicator color={refresh.tintColor} />}
        </div>
      ) : null}
      <div
        ref={content}
        className={cx('rn-view', 'rn-scroll-content', horizontal && 'rn-scroll-content-h')}
        style={sx(contentContainerStyle)}
      >
        {children}
      </div>
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/* FlatList                                                                   */
/* -------------------------------------------------------------------------- */

export type FlatListProps<T> = {
  data: readonly T[] | null | undefined;
  renderItem: (info: { item: T; index: number }) => ReactNode;
  keyExtractor?: (item: T, index: number) => string;
  ListEmptyComponent?: ReactNode;
  ListHeaderComponent?: ReactNode;
  ListFooterComponent?: ReactNode;
  ItemSeparatorComponent?: React.ComponentType;
  inverted?: boolean;
  /**
   * Accepted and ignored. Both exist so RN can measure and jump within a
   * virtualised list; nothing here is virtualised, so the browser lays the whole
   * list out. Keeping them in the type means a ported list compiles unchanged.
   */
  getItemLayout?: (data: unknown, index: number) => { length: number; offset: number; index: number };
  initialScrollIndex?: number;
  onEndReached?: () => void;
  onEndReachedThreshold?: number;
} & Omit<ScrollViewProps, 'children'>;

/**
 * RN's FlatList without the virtualisation. The lists in this app are short
 * (a match's chat page, a fan's tickets, a season's passes) and the browser's
 * own paint scheduling handles them; a windowing library would add a dependency
 * and a scroll-restoration bug for no measured gain.
 */
export const FlatList = forwardRef(function FlatList<T>(
  {
    data,
    renderItem,
    keyExtractor,
    ListEmptyComponent,
    ListHeaderComponent,
    ListFooterComponent,
    ItemSeparatorComponent,
    inverted,
    contentContainerStyle,
    getItemLayout: _getItemLayout,
    initialScrollIndex: _initialScrollIndex,
    onEndReached: _onEndReached,
    onEndReachedThreshold: _onEndReachedThreshold,
    ...scroll
  }: FlatListProps<T>,
  ref: Ref<ScrollViewRef>,
) {
  const items = data ?? [];
  return (
    <ScrollView
      ref={ref}
      {...scroll}
      contentContainerStyle={[
        contentContainerStyle,
        inverted ? { flexDirection: 'column-reverse' } : null,
      ]}
    >
      {ListHeaderComponent}
      {items.length === 0
        ? ListEmptyComponent
        : items.map((item, index) => (
            <Fragment key={keyExtractor ? keyExtractor(item, index) : String(index)}>
              {index > 0 && ItemSeparatorComponent ? <ItemSeparatorComponent /> : null}
              {renderItem({ item, index })}
            </Fragment>
          ))}
      {ListFooterComponent}
    </ScrollView>
  );
}) as <T>(p: FlatListProps<T> & { ref?: Ref<ScrollViewRef> }) => React.ReactElement;

/* -------------------------------------------------------------------------- */
/* SafeAreaView                                                               */
/* -------------------------------------------------------------------------- */

export type Edge = 'top' | 'bottom' | 'left' | 'right';

/**
 * Honours the device's safe-area insets via `env(safe-area-inset-*)` — which is
 * what `react-native-safe-area-context` reads on iOS anyway. Relevant when the
 * app is installed to a home screen and runs without browser chrome.
 */
export function SafeAreaView({
  children,
  style,
  edges = ['top', 'bottom', 'left', 'right'],
  className,
  pointerEvents,
}: {
  children?: ReactNode;
  style?: StyleProp;
  edges?: readonly Edge[];
  className?: string;
  pointerEvents?: 'auto' | 'none' | 'box-none';
}) {
  const has = (e: Edge) => edges.includes(e);
  return (
    <div
      className={cx('rn-view', pointerEvents === 'box-none' && 'rn-box-none', className)}
      style={{
        ...(pointerEvents && pointerEvents !== 'box-none' ? { pointerEvents } : null),
        paddingTop: has('top') ? 'env(safe-area-inset-top)' : undefined,
        paddingBottom: has('bottom') ? 'env(safe-area-inset-bottom)' : undefined,
        paddingLeft: has('left') ? 'env(safe-area-inset-left)' : undefined,
        paddingRight: has('right') ? 'env(safe-area-inset-right)' : undefined,
        ...sx(style),
      }}
    >
      {children}
    </div>
  );
}

/** Insets as numbers, for the few places that position against them by hand. */
export function useSafeAreaInsets() {
  const [insets, setInsets] = useState({ top: 0, bottom: 0, left: 0, right: 0 });
  useEffect(() => {
    const read = () => {
      const probe = document.createElement('div');
      probe.style.cssText =
        'position:fixed;visibility:hidden;top:env(safe-area-inset-top);bottom:env(safe-area-inset-bottom);' +
        'left:env(safe-area-inset-left);right:env(safe-area-inset-right)';
      document.body.appendChild(probe);
      const cs = getComputedStyle(probe);
      setInsets({
        top: parseFloat(cs.top) || 0,
        bottom: parseFloat(cs.bottom) || 0,
        left: parseFloat(cs.left) || 0,
        right: parseFloat(cs.right) || 0,
      });
      probe.remove();
    };
    read();
    window.addEventListener('resize', read);
    window.addEventListener('orientationchange', read);
    return () => {
      window.removeEventListener('resize', read);
      window.removeEventListener('orientationchange', read);
    };
  }, []);
  return insets;
}

/* -------------------------------------------------------------------------- */
/* ActivityIndicator                                                          */
/* -------------------------------------------------------------------------- */

export function ActivityIndicator({
  size = 'small',
  color,
  style,
}: {
  size?: 'small' | 'large' | number;
  color?: string;
  style?: StyleProp;
}) {
  const dimension = typeof size === 'number' ? size : size === 'large' ? 36 : 20;
  return (
    <span
      role="progressbar"
      aria-label="Loading"
      className="rn-spinner"
      style={{
        width: dimension,
        height: dimension,
        borderColor: color ?? 'currentColor',
        ...sx(style),
      }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* TextInput                                                                  */
/* -------------------------------------------------------------------------- */

const INPUT_MODE: Record<string, string> = {
  'email-address': 'email',
  numeric: 'numeric',
  'number-pad': 'numeric',
  'decimal-pad': 'decimal',
  'phone-pad': 'tel',
  url: 'url',
  default: 'text',
};

export type TextInputProps = {
  value?: string;
  defaultValue?: string;
  onChangeText?: (text: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onSubmitEditing?: () => void;
  placeholder?: string;
  placeholderTextColor?: string;
  secureTextEntry?: boolean;
  keyboardType?: keyof typeof INPUT_MODE | string;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  autoCorrect?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  maxLength?: number;
  editable?: boolean;
  style?: StyleProp;
  /** Tints the text-selection highlight, like RN's. */
  selectionColor?: string;
  /**
   * Android autofill opt-out. Samsung's fill service walks a form and bounces
   * focus between fields; the browser equivalent of refusing it is
   * `autocomplete="off"`, which this maps to.
   */
  importantForAutofill?: string;
  spellCheck?: boolean;
  textContentType?: string;
  returnKeyType?: string;
  blurOnSubmit?: boolean;
  accessibilityLabel?: string;
  testID?: string;
};

export const TextInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextInputProps>(
  function TextInput(
    {
      value,
      defaultValue,
      onChangeText,
      onBlur,
      onFocus,
      onSubmitEditing,
      placeholder,
      placeholderTextColor,
      secureTextEntry,
      keyboardType = 'default',
      autoCapitalize,
      autoCorrect,
      autoComplete,
      autoFocus,
      multiline,
      numberOfLines,
      maxLength,
      editable = true,
      style,
      selectionColor,
      importantForAutofill,
      spellCheck,
      accessibilityLabel,
      testID,
    },
    ref,
  ) {
    const id = useId();
    const shared = {
      id,
      'data-testid': testID,
      className: 'rn-input',
      value,
      defaultValue,
      placeholder,
      maxLength,
      autoFocus,
      readOnly: !editable,
      'aria-label': accessibilityLabel,
      autoCapitalize,
      autoComplete: (importantForAutofill && importantForAutofill !== 'auto'
        ? 'off'
        : autoComplete) as string | undefined,
      spellCheck: spellCheck ?? autoCorrect,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeText?.(e.target.value),
      onBlur,
      onFocus,
      style: {
        ...(placeholderTextColor
          ? ({ ['--rn-placeholder' as string]: placeholderTextColor } as CSSProperties)
          : null),
        ...(selectionColor
          ? ({ ['--rn-selection' as string]: selectionColor } as CSSProperties)
          : null),
        ...sx(style),
      },
    };

    if (multiline) {
      return (
        <textarea
          {...shared}
          ref={ref as Ref<HTMLTextAreaElement>}
          rows={numberOfLines ?? 3}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSubmitEditing?.();
          }}
        />
      );
    }

    return (
      <input
        {...shared}
        ref={ref as Ref<HTMLInputElement>}
        type={secureTextEntry ? 'password' : keyboardType === 'email-address' ? 'email' : 'text'}
        inputMode={INPUT_MODE[keyboardType] as never}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmitEditing?.();
        }}
      />
    );
  },
);

/* -------------------------------------------------------------------------- */
/* Image / Switch / Modal / KeyboardAvoidingView                              */
/* -------------------------------------------------------------------------- */

export function Image({
  source,
  style,
  resizeMode,
  accessibilityLabel,
  onError,
}: {
  source: { uri?: string } | string | undefined;
  style?: StyleProp;
  resizeMode?: 'cover' | 'contain' | 'stretch' | 'center';
  accessibilityLabel?: string;
  onError?: () => void;
}) {
  const uri = typeof source === 'string' ? source : source?.uri;
  const objectFit =
    resizeMode === 'stretch' ? 'fill' : resizeMode === 'center' ? 'none' : resizeMode;

  // RN's `tintColor` recolours an image's opaque pixels and keeps its alpha —
  // how the club mark is drawn white on green and green on white from one green
  // source file. `<img>` has no such property, but a CSS mask does exactly this:
  // the artwork becomes the stencil and the colour is painted through it.
  const tint = flatten(style).tintColor as string | undefined;
  if (tint && uri) {
    return (
      <span
        role="img"
        aria-label={accessibilityLabel || undefined}
        aria-hidden={accessibilityLabel ? undefined : true}
        className="rn-image rn-image-tinted"
        style={{
          maskImage: 'url(' + uri + ')',
          WebkitMaskImage: 'url(' + uri + ')',
          maskSize: objectFit === 'cover' ? 'cover' : 'contain',
          WebkitMaskSize: objectFit === 'cover' ? 'cover' : 'contain',
          backgroundColor: tint,
          ...sx(style),
        }}
      />
    );
  }

  return (
    <img
      className="rn-image"
      src={uri}
      alt={accessibilityLabel ?? ''}
      loading="lazy"
      decoding="async"
      onError={onError}
      style={{ objectFit, ...sx(style) }}
    />
  );
}

export function Switch({
  value,
  onValueChange,
  disabled,
  trackColor,
  thumbColor,
  accessibilityLabel,
}: {
  value?: boolean;
  onValueChange?: (v: boolean) => void;
  disabled?: boolean;
  trackColor?: { false?: string; true?: string };
  thumbColor?: string;
  accessibilityLabel?: string;
}) {
  return (
    <label className="rn-switch" aria-disabled={disabled}>
      <input
        type="checkbox"
        checked={!!value}
        disabled={disabled}
        aria-label={accessibilityLabel}
        onChange={(e) => onValueChange?.(e.target.checked)}
      />
      <span
        className="rn-switch-track"
        style={{
          background: value ? trackColor?.true : trackColor?.false,
        }}
      >
        <span className="rn-switch-thumb" style={{ background: thumbColor }} />
      </span>
    </label>
  );
}

export function Modal({
  visible,
  onRequestClose,
  children,
  transparent,
  animationType,
}: {
  visible?: boolean;
  onRequestClose?: () => void;
  children?: ReactNode;
  transparent?: boolean;
  animationType?: 'none' | 'slide' | 'fade';
  /** Android-only on native; the browser has no OS status bar to draw under. */
  statusBarTranslucent?: boolean;
}) {
  useEffect(() => {
    if (!visible || !onRequestClose) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onRequestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, onRequestClose]);

  useEffect(() => {
    if (!visible) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  if (!visible) return null;

  return createPortal(
    <div
      className={cx('rn-modal', transparent && 'rn-modal-transparent', animationType === 'slide' && 'rn-modal-slide')}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onRequestClose?.();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/**
 * The browser moves the viewport for the on-screen keyboard by itself, so this
 * has no work to do — and when the caller gave it no style of its own, it draws
 * no box either.
 *
 * That matters for more than tidiness. A bottom sheet is `<fill> > <KAV> >
 * <sheet maxHeight: '85%'>`; Yoga resolves that 85% against the space available,
 * but CSS resolves a percentage height against a DEFINITE containing block and
 * an auto-height KAV is not one, so the sheet would grow past the screen.
 * `display: contents` removes the box from the layout entirely and the sheet
 * measures against the full-height parent, which is what the native app does.
 * A KAV that WAS given a style (`flex: 1`, say) is load-bearing and stays a box.
 */
export function KeyboardAvoidingView({
  children,
  style,
}: {
  children?: ReactNode;
  style?: StyleProp;
  behavior?: string;
  keyboardVerticalOffset?: number;
}) {
  const hasStyle = Object.keys(flatten(style)).length > 0;
  if (!hasStyle) return <div style={{ display: 'contents' }}>{children}</div>;
  return <View style={style}>{children}</View>;
}

/* -------------------------------------------------------------------------- */
/* Dimensions                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * RN's `useWindowDimensions`. Reports the APP SHELL's box rather than the
 * browser window's: on a desktop the app is framed to phone width, and a screen
 * that sized a seat map to `window.innerWidth` would draw it several times
 * wider than the frame it sits in.
 */
export function useWindowDimensions(): {
  width: number;
  height: number;
  scale: number;
  fontScale: number;
} {
  const [size, setSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));

  useEffect(() => {
    const shell = document.querySelector('.app-shell');
    const read = () =>
      setSize({
        width: shell?.clientWidth || window.innerWidth,
        height: shell?.clientHeight || window.innerHeight,
      });
    read();
    if (!shell) {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(shell);
    return () => ro.disconnect();
  }, []);

  return { ...size, scale: window.devicePixelRatio || 1, fontScale: 1 };
}

/**
 * RN exports these as classes, so a screen can write `useRef<FlatList>(null)`
 * and call `.scrollToEnd()` on it. Here they are functions, so the same names
 * are also declared as types pointing at the imperative handle each one
 * exposes — a value and a type may share a name in TypeScript.
 */
export type FlatList<_ItemT = unknown> = ScrollViewRef;
export type TextInput = HTMLInputElement & HTMLTextAreaElement;

/** RN event shapes the ported screens name in their handler signatures.
 *  `GestureResponderEvent` is React's own base synthetic event: it is what a
 *  DOM click actually delivers, and it is also what react-hook-form's
 *  `handleSubmit(...)` accepts, so a submit handler passes straight to a
 *  button's `onPress` the way it does on native. */
export type LayoutChangeEvent = { nativeEvent: { layout: { width: number; height: number } } };
export type GestureResponderEvent = BaseSyntheticEvent;

/* -------------------------------------------------------------------------- */

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export type { Style, StyleProp };
